import { useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const REVEAL_INTERVAL_S = 10;

const PODIUM = {
  1: { bg: '#FFD700', color: '#7a5100', label: '🥇' },
  2: { bg: '#C0C0C0', color: '#4a4a4a', label: '🥈' },
  3: { bg: '#CD7F32', color: '#5c2e00', label: '🥉' },
};

function Countdown({ onDone }) {
  const [remaining, setRemaining] = useState(REVEAL_INTERVAL_S);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    setRemaining(REVEAL_INTERVAL_S);
  }, []);

  useEffect(() => {
    if (remaining <= 0) {
      onDoneRef.current?.();
      return;
    }
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining]);

  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Typography
        variant="h3"
        fontWeight={700}
        sx={{
          color: remaining <= 2 ? 'error.main' : 'text.secondary',
          transition: 'color 0.3s',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {remaining}
      </Typography>
    </Box>
  );
}

/**
 * Props:
 *   revealedWords  – array de RevealItemDTO ya revelados (ordenados 10→1 por el servidor)
 *   totalToReveal  – cuántas palabras hay en total (normalmente 10)
 *   isComplete     – true cuando el servidor cierra el stream
 *   onComplete     – callback cuando termina
 */
export default function TopTenReveal({ revealedWords = [], yourWords = [], totalToReveal = 10, isComplete = false, onComplete, enableConfetti = false }) {
  const { t } = useTranslation();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const confettiFired = useRef(false);

  const yourWordSet = new Set(yourWords.map((w) => w.canonical?.toLowerCase()).filter(Boolean));

  useEffect(() => {
    if (isComplete) {
      onCompleteRef.current?.();
      if (enableConfetti && !confettiFired.current) {
        confettiFired.current = true;
        confetti({ particleCount: 220, spread: 110, origin: { y: 0.5 } });
      }
    }
  }, [isComplete, enableConfetti]);

  const nextPosition = !isComplete && revealedWords.length < totalToReveal
    ? totalToReveal - revealedWords.length  // cuenta hacia atrás: #10 primero, #1 (el top) al final
    : null;

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="h6" fontWeight={700} textAlign="center" sx={{ mb: 2 }}>
        🏆 TOP 10
      </Typography>

      {[...revealedWords].sort((a, b) => a.position - b.position).map((item) => {
        const podium = PODIUM[item.position];
        const isYours = yourWordSet.has(item.word?.toLowerCase());
        return (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1,
              mb: 0.5,
              borderRadius: 2,
              bgcolor: podium ? podium.bg + '33' : isYours ? '#e3f2fd' : 'grey.50',
              animation: `${slideDown} 0.35s ease-out`,
              border: podium ? `1px solid ${podium.bg}88` : 'none',
            }}
          >
            <Typography
              variant={podium ? 'h6' : 'body1'}
              sx={{ minWidth: 36, textAlign: 'center', fontWeight: 700 }}
            >
              {podium ? podium.label : item.position}
            </Typography>
            <Typography
              fontWeight={podium ? 700 : 500}
              sx={{ flex: 1, textTransform: 'capitalize', fontSize: podium ? '1.05rem' : 'inherit' }}
            >
              {item.displayWord ?? item.word}
            </Typography>
            {isYours && (
              <Chip
                label={t('results.yourWordBadge')}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28, textAlign: 'right' }}>
              {item.amount}x
            </Typography>
          </Box>
        );
      })}

      {!isComplete && revealedWords.length < totalToReveal && (
        <Box sx={{ textAlign: 'center', py: 1 }}>
          {nextPosition && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, opacity: 0.7 }}>
              #{nextPosition} en...
            </Typography>
          )}
          {/* Cuenta atrás cosmética: el reveal real lo controla el servidor */}
          <Countdown key={revealedWords.length} />
        </Box>
      )}
    </Box>
  );
}
